import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface AppRow {
  readonly applicationId: string;
  readonly appCode: string;
  readonly appName: string;
  readonly appType: string;
  readonly publishScope: string;
  readonly lifecycleStatus: string;
  readonly isMarketplaceListed: boolean;
  readonly currentVersionNo: number;
}

/**
 * M40 — LCNC Marketplace (GR_M36-M40 §L L40.04 / §Q13). Browses the applications a
 * tenant may install and drives the least-privilege install flow: the tenant admin
 * picks an app, reviews the permissions the listing DECLARES it needs, and approves
 * a subset — the backend grants only the intersection of declared ∩ approved, runs
 * the app inside a tenant-scoped namespace and caps installs per tenant. Installing
 * is gated by TENANT_INSTALL_MANAGE; a cross-tenant install can never leak because
 * every write is tenant-scoped (LAW 4). Browsing needs APP_VIEW.
 */
export function MarketplaceConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [selected, setSelected] = useState<AppRow | null>(null);
  const [installCode, setInstallCode] = useState('');
  const [declaredPermissions, setDeclaredPermissions] = useState('');
  const [approvedPermissions, setApprovedPermissions] = useState('');
  const [busy, setBusy] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [installed, setInstalled] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    apiRequest<{ data: AppRow[] }>(`/v1/app-platform/applications?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, search, reload]);

  const openInstall = (a: AppRow): void => {
    setSelected(a);
    setInstallCode(`INST-${a.appCode}`);
    setDeclaredPermissions('');
    setApprovedPermissions('');
    setInstallError(null);
    setInstalled(null);
  };

  const parseList = (s: string): string[] =>
    s.split(',').map((x) => x.trim()).filter((x) => x.length > 0);

  const submitInstall = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selected) return;
    setInstallError(null);
    setInstalled(null);
    if (!installCode.trim()) { setInstallError(t('m40.marketplace.installCodeRequired')); return; }
    setBusy(true);
    try {
      await apiRequest('/v1/app-platform/installs', {
        method: 'POST',
        body: {
          installCode: installCode.trim(),
          appId: selected.applicationId,
          appCode: selected.appCode,
          declaredPermissions: parseList(declaredPermissions),
          approvedPermissions: approvedPermissions.trim() ? parseList(approvedPermissions) : undefined,
        },
        token,
      });
      setInstalled(t('m40.marketplace.installedAs', { name: selected.appName, code: installCode.trim() }));
      setSelected(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setInstallError(t('m40.marketplace.notAuth'));
      } else {
        setInstallError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="lcnc-marketplace">
      <h1>{t('m40.marketplace.title')}</h1>

      <section aria-label="marketplace-search">
        <label>{t('m40.common.search')}
          <input aria-label="marketplace-search-input" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </section>

      {installed && <p aria-label="install-success">{installed}</p>}
      {error && <p role="alert">{error}</p>}

      {loading
        ? <p>{t('m40.common.loading')}</p>
        : (
          <DataTable<AppRow>
            ariaLabel="marketplace-table"
            testId="marketplace-table"
            rowKey={(a) => a.applicationId}
            rowAriaLabel={(a) => `app-${a.appCode}`}
            emptyMessage={t('m40.marketplace.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (a) => a.appCode },
              { key: 'name', header: t('m40.common.name'), render: (a) => a.appName },
              { key: 'type', header: t('m40.common.type'), render: (a) => a.appType },
              { key: 'scope', header: t('m40.common.scope'), render: (a) => a.publishScope },
              { key: 'status', header: t('m40.common.status'), render: (a) => a.lifecycleStatus },
              { key: 'version', header: t('m40.common.version'), render: (a) => `v${a.currentVersionNo}` },
              { key: 'listed', header: t('m40.marketplace.colListed'), render: (a) => a.isMarketplaceListed ? t('m40.common.yes') : t('m40.common.no') },
              { key: 'actions', header: t('m40.common.actions'), render: (a) => (
                <Button variant="primary" type="button" aria-label={`install-${a.appCode}`} onClick={() => openInstall(a)}>{t('m40.marketplace.install')}</Button>
              ) },
            ]}
          />
        )}

      {selected && (
        <section aria-label="install-dialog">
          <h2>{t('m40.marketplace.installHeading', { name: selected.appName })}</h2>
          <form onSubmit={submitInstall} aria-label="install-form">
            <label>{t('m40.marketplace.installCode')}
              <input aria-label="install-code" value={installCode} onChange={(e) => setInstallCode(e.target.value)} />
            </label>
            <label>{t('m40.marketplace.declaredPerms')}
              <input
                aria-label="declared-permissions"
                value={declaredPermissions}
                onChange={(e) => setDeclaredPermissions(e.target.value)}
                placeholder="APP_VIEW, REPORT_VIEW"
              />
            </label>
            <label>{t('m40.marketplace.approvedPerms')}
              <input
                aria-label="approved-permissions"
                value={approvedPermissions}
                onChange={(e) => setApprovedPermissions(e.target.value)}
                placeholder={t('m40.marketplace.approvedPlaceholder')}
              />
            </label>
            <Button variant="primary" type="submit" disabled={busy}>{busy ? t('m40.marketplace.installing') : t('m40.marketplace.confirmInstall')}</Button>
            <Button variant="danger" type="button" onClick={() => setSelected(null)}>{t('m40.common.cancel')}</Button>
          </form>
          {installError && <p role="alert">{installError}</p>}
        </section>
      )}
    </main>
  );
}
